/**
 * Column definitions for the Employees DataTable — Phase-2 W3.T4.
 *
 * Kept in its own module so the page component stays lean and the
 * column matrix is easy to tweak without rebasing the page.
 *
 * The table is `manualSorting` (server-side); the page wires a single
 * `?sort=` URL param that maps onto the sort state stored in this
 * column set. Only `fullName` and `createdAt` are sortable in Phase 2
 * because those are the BE indices we already own (research-A §3).
 *
 * RBAC for the row Action column lives one layer up — we render a kebab
 * trigger here and the page chooses which menu items to show based on
 * the actor + the row.
 */
import * as React from 'react';
import { MoreHorizontal, Trash2 } from 'lucide-react';

import type { RoleSlug, UserSummary, AccessLevel } from '@aris/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type ColumnDef, createColumnHelper } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate } from '@/lib/date';

import type { DepartmentRow } from '@/api/departments';
import type { RoleRow } from '@/api/roles';

const helper = createColumnHelper<UserSummary>();

// ─── Status / role-tier styling ──────────────────────────────────────────

function statusBadge(status: UserSummary['status']): React.ReactElement {
  if (status === 'ACTIVE') return <Badge variant="active">Активен</Badge>;
  if (status === 'PENDING') return <Badge variant="pending">Ожидает</Badge>;
  return <Badge variant="archived">Архив</Badge>;
}

function roleTierVariant(level: AccessLevel) {
  switch (level) {
    case 'ADMIN':
      return 'admin' as const;
    case 'SUPERVISOR':
      return 'supervisor' as const;
    case 'MANAGER':
      return 'manager' as const;
    default:
      return 'employee' as const;
  }
}

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || '·';
  const second = parts[1] ?? '';
  const a = first[0] ?? '';
  const b = second[0] ?? '';
  return (a + b).toUpperCase() || '·';
}

// ─── Action menu ─────────────────────────────────────────────────────────

export interface ActorContext {
  /** Self id — used by visibility helpers (e.g. self-delete is forbidden). */
  id: string | null;
  accessLevel: AccessLevel;
  /** Used to decide whether РО can issue a reset on a peer/lead. */
  departmentId: number | null;
  /**
   * The actor's role slug. Under the GLOBAL admin-appointment rule an
   * ASSISTANT_GIP is a full generic-admin for NON-admin user management, so
   * this slug NO LONGER gates any row action — an assistant flows through
   * the same ADMIN branch as a generic admin below (archive/reset/hard-
   * delete/reissue of MANAGER-tier targets). Retained for actor-shape
   * symmetry with `DetailActor` / `AddActor` and possible future per-role
   * UI hooks; `undefined` means «unknown slug» (treated as a generic admin).
   */
  roleSlug?: RoleSlug;
}

export interface RowActionsProps {
  row: UserSummary;
  actor: ActorContext;
  onOpen: (row: UserSummary) => void;
  onArchive: (row: UserSummary) => void;
  onHardDelete: (row: UserSummary) => void;
  onReissueInvite: (row: UserSummary) => void;
  onIssueReset: (row: UserSummary) => void;
}

/**
 * Pure visibility helpers — exported so the test file can assert the
 * RBAC matrix without needing to render the menu and wrestle with
 * Radix's portal.
 *
 * `canArchive` returns true ONLY for non-PENDING rows (ACTIVE today —
 * ARCHIVED is filtered above). PENDING rows surface the destructive
 * "Удалить" action via `canHardDelete` instead, since archiving an
 * invitee that never accepted is overkill (no history to preserve).
 */
export function canArchive(row: UserSummary, actor: ActorContext): boolean {
  if (row.status !== 'ACTIVE') return false;
  if (actor.accessLevel === 'ADMIN') {
    // RTF L79: Admin handles only Admin/РО tiers. Сотрудник-tier
    // archive is the РО's job — hide the action for cross-tier targets
    // so we don't surface a 403-bound click. Admin → Admin is also
    // hidden (last-admin / peer-tier protection).
    return row.accessLevel === 'MANAGER';
  }
  // РО archives within own dept only AND only EMPLOYEE-tier targets —
  // server is the firm guard, but we hide the action so the optimistic
  // flash doesn't happen.
  if (actor.accessLevel === 'MANAGER') {
    if (row.accessLevel !== 'EMPLOYEE') return false;
    return row.departmentId !== null && row.departmentId === actor.departmentId;
  }
  return false;
}

/**
 * Hard-delete (Phase 2 W1.T8) is the PENDING-only counterpart of archive.
 * RBAC mirrors archive (RTF L79) plus the supervisor-invites-admin
 * amendment (2026-06-10) and the GLOBAL admin-lifecycle rule (fix-pack):
 *   - Admin → MANAGER-tier PENDING only. Under the GLOBAL rule a
 *     non-supervisor admin cannot mutate an ADMIN-tier user AT ALL — the BE
 *     now 403s hard-delete of an ADMIN-tier target too (not just
 *     appointment) — so the ADMIN-target arm is supervisor-only.
 *   - SUPERVISOR → ADMIN-tier PENDING only (tier EQUALITY — mirrors the
 *     invite/reissue rule; the supervisor cleans up the very ADMIN-tier
 *     invites it alone can create).
 *   - РО → own-dept EMPLOYEE-tier PENDING.
 *   - Self → never (the BE 403s anyway; defense in depth).
 */
export function canHardDelete(
  row: UserSummary,
  actor: ActorContext,
  selfId: string | null,
): boolean {
  if (row.status !== 'PENDING') return false;
  if (selfId !== null && row.id === selfId) return false;
  if (actor.accessLevel === 'ADMIN') {
    // GLOBAL admin-lifecycle rule (fix-pack): a non-supervisor admin (generic
    // ADMIN or ASSISTANT_GIP) manages only NON-admin users — the BE now 403s
    // ANY write against an ADMIN-tier target, hard-delete included. So the
    // admin branch is MANAGER-tier only; an ADMIN-tier PENDING invite is
    // cleaned up by the SUPERVISOR (branch below), the sole actor that can
    // create it.
    return row.accessLevel === 'MANAGER';
  }
  if (actor.accessLevel === 'SUPERVISOR') {
    // supervisor-invites-admin: ADMIN-tier PENDING only (tier EQUALITY).
    return row.accessLevel === 'ADMIN';
  }
  if (actor.accessLevel === 'MANAGER') {
    if (row.accessLevel !== 'EMPLOYEE') return false;
    return row.departmentId !== null && row.departmentId === actor.departmentId;
  }
  return false;
}

export function canReissueInvite(row: UserSummary, actor: ActorContext): boolean {
  if (row.status !== 'PENDING') return false;
  if (actor.accessLevel === 'ADMIN') {
    // GLOBAL rule: re-issuing an ADMIN-tier invite re-mints a live
    // admin-tier appointment an accomplice can accept → it is an
    // APPOINTMENT path (BE P2 → supervisor-only). So a non-supervisor
    // admin (generic ADMIN, ГИП, ASSISTANT_GIP) may reissue MANAGER-tier
    // (РО/DEPT_HEAD) invites only — never ADMIN-tier. Сотрудник invites
    // belong to РО.
    return row.accessLevel === 'MANAGER';
  }
  if (actor.accessLevel === 'SUPERVISOR') {
    // supervisor-invites-admin: ADMIN-tier PENDING only (tier EQUALITY) —
    // the SUPERVISOR is the sole actor that may reissue an admin invite.
    return row.accessLevel === 'ADMIN';
  }
  if (actor.accessLevel === 'MANAGER') {
    if (row.accessLevel !== 'EMPLOYEE') return false;
    return row.departmentId !== null && row.departmentId === actor.departmentId;
  }
  return false;
}

/**
 * Issue-password-reset visibility. Mirrors
 * `password-reset.service.ts:assertCanIssue` (BE source of truth, lines
 * ~665-695) — this is the FE-side gate so we never surface a button the
 * BE will 403:
 *   - Self → never (BE refuses self-reset; defense in depth).
 *   - EMPLOYEE / SUPERVISOR actors → never.
 *   - MANAGER actor → own-dept EMPLOYEE-tier targets only.
 *   - ADMIN actor → MANAGER-tier targets only. NOT EMPLOYEE (РО's surface),
 *     NOT SUPERVISOR (peer-tier protection), and — under the GLOBAL rule
 *     (ADD #1) — NOT ADMIN-tier: resetting another admin's password is an
 *     account-takeover vector (issue() returns a live /reset link even in
 *     prod), so it is SUPERVISOR-only via the SEPARATE emergency-reset
 *     surface, never this row button. This is the FE mirror of the BE
 *     `assertCanIssue` ADMIN branch, now MANAGER-only.
 *
 * R6.13 / D-01 round 2 (DA HIGH): the previous version returned `true`
 * for any ACTIVE row when actor was ADMIN, which surfaced the button for
 * ADMIN→EMPLOYEE and ADMIN→SUPERVISOR — both of which 403 on the BE. The
 * same alignment bug as the archive button, now fixed for the reset
 * surface too.
 */
export function canIssueReset(
  row: UserSummary,
  actor: ActorContext,
  selfId: string | null = actor.id,
): boolean {
  if (row.status !== 'ACTIVE') return false;
  if (selfId !== null && row.id === selfId) return false;
  if (actor.accessLevel === 'ADMIN') {
    // ADD #1: MANAGER-tier only. Reset-of-admin is the takeover vector,
    // closed to every non-supervisor admin (there is NO supervisor arm
    // here — the supervisor recovers admin accounts via the emergency
    // endpoint, not this row action).
    return row.accessLevel === 'MANAGER';
  }
  if (actor.accessLevel === 'MANAGER') {
    // РО can only reset employees in own dept whose tier is below them
    // (i.e. EMPLOYEE). Hides the action for peer-or-above users.
    if (row.departmentId !== actor.departmentId) return false;
    return row.accessLevel === 'EMPLOYEE';
  }
  return false;
}

function RowActions({
  row,
  actor,
  onOpen,
  onArchive,
  onHardDelete,
  onReissueInvite,
  onIssueReset,
}: RowActionsProps): React.ReactElement {
  // Stop the row-click handler from firing when the user opens the
  // kebab — otherwise both the menu AND the detail modal would surface.
  const stop = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const showArchive = canArchive(row, actor);
  const showHardDelete = canHardDelete(row, actor, actor.id);
  const showReissue = canReissueInvite(row, actor);
  const showReset = canIssueReset(row, actor);

  return (
    <div className="flex justify-end" onClick={stop} onKeyDown={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Действия с пользователем ${row.fullName}`}
            data-testid={`row-actions-trigger-${row.id}`}
          >
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid={`row-actions-menu-${row.id}`}>
          <DropdownMenuItem onSelect={() => onOpen(row)}>Открыть</DropdownMenuItem>
          {showReissue ? (
            <DropdownMenuItem onSelect={() => onReissueInvite(row)}>
              Перевыпустить приглашение
            </DropdownMenuItem>
          ) : null}
          {showReset ? (
            <DropdownMenuItem onSelect={() => onIssueReset(row)}>Сбросить пароль</DropdownMenuItem>
          ) : null}
          {showArchive ? (
            <DropdownMenuItem onSelect={() => onArchive(row)}>Архивировать</DropdownMenuItem>
          ) : null}
          {showHardDelete ? (
            <DropdownMenuItem
              onSelect={() => onHardDelete(row)}
              className="text-destructive focus:text-destructive"
              data-testid={`row-action-hard-delete-${row.id}`}
            >
              <Trash2 className="mr-2 size-4" aria-hidden="true" />
              Удалить
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ─── Cell content helpers ────────────────────────────────────────────────

function NameCell({ row }: { row: UserSummary }): React.ReactElement {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <span
        aria-hidden
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-[11px] font-semibold text-accent-foreground"
      >
        {initialsOf(row.fullName)}
      </span>
      <span className="truncate font-medium text-ink" title={row.fullName}>
        {row.fullName}
      </span>
    </div>
  );
}

function EmailCell({ row }: { row: UserSummary }): React.ReactElement {
  // Title shows the full address on hover (the cell can truncate).
  return (
    <span className="block max-w-[260px] truncate text-muted" title={row.email}>
      {row.email}
    </span>
  );
}

function DepartmentCell({
  row,
  departments,
}: {
  row: UserSummary;
  departments: ReadonlyMap<number, DepartmentRow>;
}): React.ReactElement {
  if (row.departmentId === null) {
    return <span className="text-muted">—</span>;
  }
  const dept = departments.get(row.departmentId);
  return (
    <Badge variant="outline" className="font-mono text-[11px]">
      {dept ? dept.slug : `#${row.departmentId}`}
    </Badge>
  );
}

function RoleCell({
  row,
  roles,
}: {
  row: UserSummary;
  roles: ReadonlyMap<number, RoleRow>;
}): React.ReactElement {
  const role = roles.get(row.roleId);
  return (
    <Badge variant={roleTierVariant(row.accessLevel)}>{role ? role.name : row.roleSlug}</Badge>
  );
}

// ─── Column factory ──────────────────────────────────────────────────────

export interface BuildColumnsArgs {
  actor: ActorContext;
  /** Dept id → row map. Lookup-friendly snapshot built from useDepartmentsQuery. */
  departments: ReadonlyMap<number, DepartmentRow>;
  /** Role id → row map. */
  roles: ReadonlyMap<number, RoleRow>;
  onOpen: (row: UserSummary) => void;
  onArchive: (row: UserSummary) => void;
  onHardDelete: (row: UserSummary) => void;
  onReissueInvite: (row: UserSummary) => void;
  onIssueReset: (row: UserSummary) => void;
}

export function buildEmployeeColumns(args: BuildColumnsArgs): ColumnDef<UserSummary, unknown>[] {
  const {
    actor,
    departments,
    roles,
    onOpen,
    onArchive,
    onHardDelete,
    onReissueInvite,
    onIssueReset,
  } = args;

  // TanStack's `createColumnHelper.accessor(key, …)` infers the value type
  // from the property type; the DataTable signature wants the looser
  // `ColumnDef<UserSummary, unknown>[]`, so we widen via `as` here. The
  // rendered cell receives the correct row type at runtime — this is only
  // a type-system relaxation.
  const cols = [
    helper.accessor('fullName', {
      header: 'ФИО',
      enableSorting: true,
      cell: ({ row }) => <NameCell row={row.original} />,
    }),
    helper.accessor('email', {
      header: 'Email',
      enableSorting: false,
      cell: ({ row }) => <EmailCell row={row.original} />,
    }),
    helper.accessor('phone', {
      header: 'Телефон',
      enableSorting: false,
      cell: ({ row }) => <span className="text-muted">{row.original.phone ?? '—'}</span>,
    }),
    helper.display({
      id: 'department',
      header: 'Отдел',
      cell: ({ row }) => <DepartmentCell row={row.original} departments={departments} />,
    }),
    helper.display({
      id: 'role',
      header: 'Роль',
      cell: ({ row }) => <RoleCell row={row.original} roles={roles} />,
    }),
    helper.accessor('status', {
      header: 'Статус',
      enableSorting: false,
      cell: ({ row }) => statusBadge(row.original.status),
    }),
    helper.accessor('createdAt', {
      header: 'Создан',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-muted text-[12px]">
          {formatDate(row.original.createdAt, 'dd.MM.yyyy')}
        </span>
      ),
    }),
    helper.display({
      id: 'actions',
      header: () => <span className="sr-only">Действия</span>,
      cell: ({ row }) => (
        <RowActions
          row={row.original}
          actor={actor}
          onOpen={onOpen}
          onArchive={onArchive}
          onHardDelete={onHardDelete}
          onReissueInvite={onReissueInvite}
          onIssueReset={onIssueReset}
        />
      ),
    }),
  ];
  return cols as unknown as ColumnDef<UserSummary, unknown>[];
}
